/**
 * Article embed registry — the drop-in hook.
 * ===========================================================================
 * An article author writes a plain-text token in the body:
 *
 *     [[aai-embed:architecture-explorer]]
 *     [[aai-embed:architecture-explorer view=voice-actor]]
 *
 * and the renderer swaps it for a live React component. Adding a new embed is one entry in the
 * map below plus the component — nothing else changes. That makes this the pattern for tutorials
 * and any future interactive figure, not a one-off for the architecture diagram.
 *
 * ─── ⭐ WHY A TEXT TOKEN AND NOT AN HTML ELEMENT ────────────────────────────────────────────
 *
 * Article bodies pass through TWO sanitisers: Froala on save, DOMPurify on render. A custom
 * element or an unusual attribute has to survive both, and either is free to strip what it does
 * not recognise — silently, and only in production content nobody re-checks. Plain text cannot be
 * stripped by a sanitiser, because it is not markup.
 *
 * It also means the author can type the token directly in the WYSIWYG. No code view, no HTML
 * knowledge, no toolbar dependency. The Article Editor offers an insert button as a convenience,
 * but the token works whether or not anyone uses it.
 */

import ArchitectureExplorer from './architecture/ArchitectureExplorer';

/**
 * name → { component, description, params }
 * `description` and `params` are shown in the editor's insert dialog, so this map is also the
 * documentation an author reads. Keep the wording aimed at them, not at us.
 */
export const EMBED_REGISTRY = {
  'architecture-explorer': {
    component: ArchitectureExplorer,
    label: 'Architecture Explorer',
    description: 'The system chain — node trees, S3 paths and operating sequences for Book '
      + 'Creators and Voice Actors.',
    params: [
      { name: 'view', values: ['book-creator', 'voice-actor', 'shared', 'storage', 'sequences'],
        default: 'book-creator', note: 'Which page opens first. All pages stay reachable.' }
    ]
  }
};

/**
 * Matches `[[aai-embed:name]]` and `[[aai-embed:name key=value key2=value2]]`.
 *
 * ⚠️ Deliberately tolerant of whitespace — a WYSIWYG will happily insert a non-breaking space or
 * wrap the token in formatting, and an author typing it by hand will not be precise. A token that
 * silently fails to render is worse than no token, because the article looks finished.
 */
export const EMBED_TOKEN = /\[\[\s*aai-embed\s*:\s*([a-z0-9-]+)\s*([^\]]*?)\s*\]\]/gi;

/** `view=voice-actor size=large` → { view: 'voice-actor', size: 'large' } */
export function parseEmbedParams(raw) {
  const params = {};
  if (!raw) return params;
  // Non-breaking spaces are the classic WYSIWYG surprise; treat them as ordinary separators.
  raw.replace(/ /g, ' ').trim().split(/\s+/).forEach((pair) => {
    const eq = pair.indexOf('=');
    if (eq > 0) params[pair.slice(0, eq)] = pair.slice(eq + 1);
  });
  return params;
}

export function resolveEmbed(name) {
  return EMBED_REGISTRY[String(name || '').toLowerCase()] || null;
}
